import { lookupGlossaryExact } from "./glossary";

export interface apiLanguage {
  /**
   * e.g. ES, DE, FR
   */
  language: string;
  name: string;
}

// Raw relay call. Keeps the relay contract intact: the dynamic-dictionary
// glossary markup (if any) rides inside the `Text` field, so no relay change is
// needed. Glossary application happens in the orchestration layer below.
export async function azureTranslate(
  text: string,
  source: string,
  target: string,
): Promise<any> {
  let endpoint = "https://api.cognitive.microsofttranslator.com";

  const data: Object = [
    {
      Text: text,
    },
  ];
  const fetchInfo: String =
    endpoint +
    "/translate?api-version=3.0&from=" +
    source +
    "&to=" +
    target +
    "&toScript=latn";

  const fetchObject: Object = {
    fetchInfo: fetchInfo,
    fetchBody: data,
  };

  const response = await fetch(
    "https://babel-api-relay.fly.dev/api/translate",
    {
      method: "POST",
      body: JSON.stringify(fetchObject),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    return { error: response.status };
  }

  const translatedData = await response.json();
  return translatedData;
}

export function azureLanguages() {
  return fetch(
    "https://api.cognitive.microsofttranslator.com/languages?api-version=3.0&scope=translation",
  )
    .then((r) => r.json())
    .catch((error) => {
      console.error(error);
      return "Could not retrieve language list.";
    });
}

// Normalised translation result for the UI. Exactly one of `error` or the
// translation fields is meaningful.
export interface TranslationResult {
  // The translated text.
  text: string;
  // Transliteration/romaji to display (may be empty).
  translit: string;
  // Human-readable error message, or null on success. When set, `text`/
  // `translit` hold the messages to show.
  error: string | null;
}

// Orchestrated translation: consults the glossary first (client-side
// short-circuit), otherwise calls the relay and maps Azure's response (and
// error shapes) into a flat TranslationResult.
//
// Why short-circuit instead of Azure dynamic-dictionary markup: the relay caps
// the `Text` field at ~50 characters (returns "TOOLONG" otherwise), and the
// `<mstrans:dictionary translation="...">word</mstrans:dictionary>` wrapper
// alone exceeds that for a single word. So forced staples are resolved locally,
// which is also free (no API round-trip) and gives the exact reading we want.
export async function translate(
  text: string,
  from: string,
  to: string,
): Promise<TranslationResult> {
  const glossaryEntry = lookupGlossaryExact(text, from, to);
  if (glossaryEntry) {
    return {
      text: glossaryEntry.translation,
      translit: glossaryEntry.translit ?? "",
      error: null,
    };
  }

  const x = await azureTranslate(text, from, to);

  if (x && x.error) {
    if (x.error === 429) {
      return {
        text: "Too many requests.",
        translit: "Please wait 15 minutes.",
        error: "429",
      };
    } else if (x.error.code === 400036) {
      return {
        text: "Invalid target language",
        translit: "Error",
        error: "400036",
      };
    } else if (x.error.code) {
      return { text: String(x.error.code), translit: "Error", error: "code" };
    } else if (x.error === "TOOLONG") {
      return {
        text: "Item too long.",
        translit: "Please input less than 50 letters.",
        error: "TOOLONG",
      };
    }
    return { text: "", translit: "Unknown error", error: "unknown" };
  }

  const translation = x?.[0]?.translations?.[0];
  const resultText = translation?.text ?? "";
  const translit = translation?.transliteration?.text ?? "";

  return { text: resultText, translit, error: null };
}
